import { useEffect, useState } from "react";
import {
  quantfolio,
  QP_APP_BASE,
  type QpClientFlow,
} from "../../../api";
import { formatDateTime } from "../format";

// The Quantfolio flow-status board on the client card: the client's live QP
// flow-instances with status pills and a per-row "Open in Quantfolio" deeplink.
// Mirrors QP's own Flows tab, read back through Andersen.
export function InvestorFlows({ investorId }: { investorId: number }) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "unlinked" }
    | { kind: "ready"; qpClientId: string; flows: QpClientFlow[] }
    | { kind: "error"; message: string }
  >({ kind: "loading" });
  const [linking, setLinking] = useState(false);

  function load() {
    setState({ kind: "loading" });
    quantfolio
      .investorFlows(investorId)
      .then((d) => {
        if (!d.linked || !d.qpClientId) setState({ kind: "unlinked" });
        else setState({ kind: "ready", qpClientId: d.qpClientId, flows: d.flows });
      })
      .catch((e) => setState({ kind: "error", message: (e as Error).message }));
  }

  useEffect(load, [investorId]);

  async function link() {
    setLinking(true);
    try {
      await quantfolio.link(investorId);
      load();
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
    } finally {
      setLinking(false);
    }
  }

  if (state.kind === "loading") return <div className="muted">Loading…</div>;

  if (state.kind === "error")
    return (
      <>
        <div className="banner-error">{state.message}</div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={load}>
          Retry
        </button>
      </>
    );

  if (state.kind === "unlinked")
    return (
      <div className="empty">
        <p>This client isn't linked to Quantfolio yet.</p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={link}
          disabled={linking}
        >
          {linking ? "Linking…" : "Link to Quantfolio"}
        </button>
      </div>
    );

  const { qpClientId, flows } = state;
  return (
    <>
      <div className="section-head">
        <div>
          <h2 className="section-title">Quantfolio flows</h2>
          <p className="muted">
            Live flow-instances for this client in the Quantfolio Platform.
          </p>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={load}>
          Refresh
        </button>
      </div>

      {flows.length === 0 ? (
        <div className="empty">
          <p>No flows for this client yet.</p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Flow</th>
              <th>Status</th>
              <th>External</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {flows.map((f) => (
              <tr key={f.id}>
                <td className="strong">
                  {f.name || f.flow?.name || "Flow"}
                  {f.name && f.flow?.name && f.name !== f.flow.name && (
                    <span className="muted flow-template"> ({f.flow.name})</span>
                  )}
                </td>
                <td>
                  <span className={`pill fstatus-${statusKey(f.status)}`}>
                    {f.status}
                  </span>
                </td>
                <td>
                  {f.externalStatus ? (
                    <span className={`pill ext-${f.externalStatus}`}>
                      {f.externalStatus}
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td className="muted">
                  {f.completedAt || f.startedAt
                    ? formatDateTime((f.completedAt || f.startedAt) as string)
                    : "—"}
                </td>
                <td>
                  <a
                    className="btn btn-ghost btn-sm"
                    href={`${QP_APP_BASE}/clients/${qpClientId}/flows/${f.id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in Quantfolio
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function statusKey(status: string): string {
  return status.toLowerCase().replace(/\s+/g, "_");
}
